import type {
  ArtPack,
  ArtPackManifest,
  AuthoredDirectionalAnimTable,
  SpriteTemplate,
} from "../../art/types";
import {
  loadAuthoredDirectionalAnimSet,
  singleFrameAnimSet,
} from "../../art/anim-set";
import affiliation from "../tables/art/packs/frontline/affiliation.json";
import packJson from "../tables/art/packs/frontline/pack.json";
import palette from "../tables/art/packs/frontline/palette.json";
import fl_t2_vanguard from "../tables/art/packs/frontline/sprites/fl_t2_vanguard.json";
import fl_t2_marksman from "../tables/art/packs/frontline/sprites/fl_t2_marksman.json";
import fl_t2_reaver from "../tables/art/packs/frontline/sprites/fl_t2_reaver.json";
import fl_t2_warden from "../tables/art/packs/frontline/sprites/fl_t2_warden.json";
import fl_t3_colossus from "../tables/art/packs/frontline/sprites/fl_t3_colossus.json";
import fl_t3_siege from "../tables/art/packs/frontline/sprites/fl_t3_siege.json";
import fl_t3_shadow from "../tables/art/packs/frontline/sprites/fl_t3_shadow.json";
import fl_t3_storm from "../tables/art/packs/frontline/sprites/fl_t3_storm.json";
import fl_t3_paladin from "../tables/art/packs/frontline/sprites/fl_t3_paladin.json";
import fx_fl_t2_vanguard_charge from "../tables/art/packs/frontline/sprites/fx_fl_t2_vanguard_charge.json";
import fx_fl_t2_marksman_muzzle from "../tables/art/packs/frontline/sprites/fx_fl_t2_marksman_muzzle.json";
import fx_fl_t2_reaver_claw from "../tables/art/packs/frontline/sprites/fx_fl_t2_reaver_claw.json";
import fx_fl_t2_warden_bash from "../tables/art/packs/frontline/sprites/fx_fl_t2_warden_bash.json";
import fx_fl_t3_colossus_pulse from "../tables/art/packs/frontline/sprites/fx_fl_t3_colossus_pulse.json";
import fx_fl_t3_siege_muzzle from "../tables/art/packs/frontline/sprites/fx_fl_t3_siege_muzzle.json";
import fx_fl_t3_shadow_tendril from "../tables/art/packs/frontline/sprites/fx_fl_t3_shadow_tendril.json";
import fx_fl_t3_storm_bolt from "../tables/art/packs/frontline/sprites/fx_fl_t3_storm_bolt.json";
import fx_fl_t3_paladin_slash from "../tables/art/packs/frontline/sprites/fx_fl_t3_paladin_slash.json";
import fl_t2_vanguardAnim from "../tables/art/packs/frontline/anims/fl_t2_vanguard.json";
import fl_t2_marksmanAnim from "../tables/art/packs/frontline/anims/fl_t2_marksman.json";
import fl_t2_reaverAnim from "../tables/art/packs/frontline/anims/fl_t2_reaver.json";
import fl_t2_wardenAnim from "../tables/art/packs/frontline/anims/fl_t2_warden.json";
import fl_t3_colossusAnim from "../tables/art/packs/frontline/anims/fl_t3_colossus.json";
import fl_t3_siegeAnim from "../tables/art/packs/frontline/anims/fl_t3_siege.json";
import fl_t3_shadowAnim from "../tables/art/packs/frontline/anims/fl_t3_shadow.json";
import fl_t3_stormAnim from "../tables/art/packs/frontline/anims/fl_t3_storm.json";
import fl_t3_paladinAnim from "../tables/art/packs/frontline/anims/fl_t3_paladin.json";
import fx_fl_t2_vanguard_chargeAnim from "../tables/art/packs/frontline/anims/fx_fl_t2_vanguard_charge.json";
import fx_fl_t2_marksman_muzzleAnim from "../tables/art/packs/frontline/anims/fx_fl_t2_marksman_muzzle.json";
import fx_fl_t2_reaver_clawAnim from "../tables/art/packs/frontline/anims/fx_fl_t2_reaver_claw.json";
import fx_fl_t2_warden_bashAnim from "../tables/art/packs/frontline/anims/fx_fl_t2_warden_bash.json";
import fx_fl_t3_colossus_pulseAnim from "../tables/art/packs/frontline/anims/fx_fl_t3_colossus_pulse.json";
import fx_fl_t3_siege_muzzleAnim from "../tables/art/packs/frontline/anims/fx_fl_t3_siege_muzzle.json";
import fx_fl_t3_shadow_tendrilAnim from "../tables/art/packs/frontline/anims/fx_fl_t3_shadow_tendril.json";
import fx_fl_t3_storm_boltAnim from "../tables/art/packs/frontline/anims/fx_fl_t3_storm_bolt.json";
import fx_fl_t3_paladin_slashAnim from "../tables/art/packs/frontline/anims/fx_fl_t3_paladin_slash.json";

const COMBAT_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  fl_t2_vanguard: fl_t2_vanguardAnim as AuthoredDirectionalAnimTable,
  fl_t2_marksman: fl_t2_marksmanAnim as AuthoredDirectionalAnimTable,
  fl_t2_reaver: fl_t2_reaverAnim as AuthoredDirectionalAnimTable,
  fl_t2_warden: fl_t2_wardenAnim as AuthoredDirectionalAnimTable,
  fl_t3_colossus: fl_t3_colossusAnim as AuthoredDirectionalAnimTable,
  fl_t3_siege: fl_t3_siegeAnim as AuthoredDirectionalAnimTable,
  fl_t3_shadow: fl_t3_shadowAnim as AuthoredDirectionalAnimTable,
  fl_t3_storm: fl_t3_stormAnim as AuthoredDirectionalAnimTable,
  fl_t3_paladin: fl_t3_paladinAnim as AuthoredDirectionalAnimTable,
  fx_fl_t2_vanguard_charge:
    fx_fl_t2_vanguard_chargeAnim as AuthoredDirectionalAnimTable,
  fx_fl_t2_marksman_muzzle:
    fx_fl_t2_marksman_muzzleAnim as AuthoredDirectionalAnimTable,
  fx_fl_t2_reaver_claw: fx_fl_t2_reaver_clawAnim as AuthoredDirectionalAnimTable,
  fx_fl_t2_warden_bash: fx_fl_t2_warden_bashAnim as AuthoredDirectionalAnimTable,
  fx_fl_t3_colossus_pulse:
    fx_fl_t3_colossus_pulseAnim as AuthoredDirectionalAnimTable,
  fx_fl_t3_siege_muzzle: fx_fl_t3_siege_muzzleAnim as AuthoredDirectionalAnimTable,
  fx_fl_t3_shadow_tendril:
    fx_fl_t3_shadow_tendrilAnim as AuthoredDirectionalAnimTable,
  fx_fl_t3_storm_bolt: fx_fl_t3_storm_boltAnim as AuthoredDirectionalAnimTable,
  fx_fl_t3_paladin_slash:
    fx_fl_t3_paladin_slashAnim as AuthoredDirectionalAnimTable,
};

const spriteList = [
  fl_t2_vanguard,
  fl_t2_marksman,
  fl_t2_reaver,
  fl_t2_warden,
  fl_t3_colossus,
  fl_t3_siege,
  fl_t3_shadow,
  fl_t3_storm,
  fl_t3_paladin,
  fx_fl_t2_vanguard_charge,
  fx_fl_t2_marksman_muzzle,
  fx_fl_t2_reaver_claw,
  fx_fl_t2_warden_bash,
  fx_fl_t3_colossus_pulse,
  fx_fl_t3_siege_muzzle,
  fx_fl_t3_shadow_tendril,
  fx_fl_t3_storm_bolt,
  fx_fl_t3_paladin_slash,
] as SpriteTemplate[];

/** Frontline T2/T3 combat kits + attack FX sprites (affiliation-rim baked). */
export function loadFrontlineArtPack(): ArtPack {
  const sprites: Record<string, SpriteTemplate> = {};
  const anims: ArtPack["anims"] = {};
  for (const s of spriteList) {
    sprites[s.id] = s;
    const authored = COMBAT_ANIMS[s.id];
    if (authored) {
      const set = loadAuthoredDirectionalAnimSet(s.id, authored);
      anims[s.id] = set;
      const idle0 = authored.dirs.e.idle.frames[0];
      if (idle0) {
        sprites[s.id] = { id: s.id, size: s.size, rows: idle0 };
      }
    } else {
      anims[s.id] = singleFrameAnimSet(s, 2);
    }
  }
  return {
    manifest: packJson as ArtPackManifest,
    palette,
    affiliation,
    sprites,
    anims,
  };
}
